import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import BenefitsCards from '@/components/BenefitsCards'
import ROIMetrics from '@/components/ROIMetrics'
import TechSpecs from '@/components/TechSpecs'
import { authService } from '@/services/auth'

function DashboardPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [displayName, setDisplayName] = useState(location.state?.user?.displayName || 'Alex')

  // Fetch user from session (needed for OIDC flow where user comes via redirect)
  useEffect(() => {
    if (!location.state?.user) {
      authService.checkSession().then((session) => {
        if (session.authenticated && session.user) {
          setDisplayName(session.user.displayName || 'Alex')
        }
      })
    }
  }, [location.state])

  return (
    <div className="min-h-screen bg-gray-50">
      <Header variant="dashboard" userName={displayName} />

      {/* Success Banner */}
      <div className="bg-gradient-to-r from-yubico-green-400 to-yubico-green-500 text-white">
        <div className="w-full px-8 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center">
                <i className="iconoir-shield-check text-yubico-green-400 text-5xl font-bold"></i>
              </div>
              <div>
                <h2 className="text-3xl font-bold mb-2">Authentication Successful</h2>
                <p className="text-yubico-green-50 text-base">
                  Logged in securely with FIDO2-certified YubiKey&reg; hardware authentication
                </p>
              </div>
            </div>
            <a
              href="https://www.yubico.com/works-with-yubikey/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0 bg-white text-yubico-green-500 font-bold px-6 py-3 rounded-xl shadow-md hover:bg-yubico-green-50 transition-all transform hover:scale-105 text-sm text-center"
            >
              What else can a YubiKey do? Click here.
            </a>
          </div>
        </div>
      </div>

      <main className="w-full px-8 py-8">
        {/* FIDO Pre-reg Benefits Section */}
        <section className="mb-12">
          <div className="text-center mb-8">
            <h3 className="text-3xl font-bold text-gray-900 mb-3">
              The Power of <span className="font-serif italic text-yubico-green-400">FIDO Pre-reg</span>
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              YubiKeys arrive pre-registered and ready to use. No manual enrollment, no IT burden,
              no user friction&mdash;just instant, phishing-resistant security from day one.
            </p>
          </div>
          <BenefitsCards />
        </section>

        {/* ROI Metrics */}
        <section className="mb-12">
          <ROIMetrics />
        </section>

        {/* CTA Section */}
        <section className="mb-12">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl overflow-hidden shadow-2xl">
            <div className="flex flex-col md:flex-row items-center">
              <div className="md:w-1/4 p-4 bg-yubico-green-400 flex items-center justify-center">
                <img
                  src="/assets/images/img-family-lineup.png"
                  alt="YubiKey Family Lineup"
                  className="w-3/4 h-auto object-contain"
                />
              </div>
              <div className="md:w-3/4 p-6">
                <h3 className="text-3xl font-bold text-white mb-3">
                  Ready to Scale Your Security?
                </h3>
                <p className="text-gray-300 text-base mb-6 leading-relaxed">
                  Deploy FIDO Pre-reg across your entire organization. Contact your Yubico&reg;
                  account team to learn about enterprise deployment, volume pricing, and
                  dedicated implementation support.
                </p>
                <div className="flex gap-4">
                  <button
                    onClick={async () => {
                      await authService.logout()
                      navigate('/')
                    }}
                    className="bg-white/10 hover:bg-white/20 text-white font-bold px-8 py-4 rounded-xl border border-white/20 transition-all"
                  >
                    Restart Demo
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Technical Specifications */}
        <section className="mb-12">
          <TechSpecs />
        </section>
      </main>

      <Footer variant="dashboard" />
    </div>
  )
}

export default DashboardPage
